import { Link } from "react-router-dom";
import { ArrowLeft, ChevronRight, Plus, Search } from "lucide-react";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const inputCls = "h-14 w-full rounded-xl border border-[#D9D3C7] bg-[#FAF8F5] px-4 text-base text-[#1B2A26] focus:outline-none focus:ring-2 focus:ring-[#2D6A4F] focus:bg-white disabled:opacity-60";

export const SelectField = ({ label, id, options, placeholder, ...p }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-sm font-semibold text-[#1B2A26]">{label}</Label>
    <select id={id} className={inputCls} {...p}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

export const TextArea = ({ label, id, rows = 3, ...p }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-sm font-semibold text-[#1B2A26]">{label}</Label>
    <textarea id={id} rows={rows} className={`${inputCls} h-auto py-3 leading-relaxed`} {...p} />
  </div>
);

export const Panel = ({ title, action, children, testId, className = "" }) => (
  <section className={`bg-white rounded-3xl border border-[#E2DDD3] p-5 sm:p-7 fade-up ${className}`} data-testid={testId}>
    {(title || action) && (
      <div className="flex items-center justify-between gap-3 mb-5">
        <h2 className="text-base md:text-lg font-bold">{title}</h2>
        {action}
      </div>
    )}
    {children}
  </section>
);

export const InfoGrid = ({ items }) => (
  <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
    {items.filter(Boolean).map(([label, value]) => (
      <div key={label} className="min-w-0">
        <dt className="text-[11px] font-bold uppercase tracking-wider text-[#5C6B66]">{label}</dt>
        <dd className="mt-1 text-[#1B2A26] break-words whitespace-pre-line">{value === 0 ? 0 : value || "—"}</dd>
      </div>
    ))}
  </dl>
);

export const Empty = ({ children, testId }) => (
  <p className="text-center py-8 text-[#5C6B66] border border-dashed border-[#E2DDD3] rounded-2xl" data-testid={testId}>{children}</p>
);

export const BackLink = ({ to, label }) => (
  <Link to={to} className="inline-flex items-center gap-2 h-11 text-[#1E4D2B] font-semibold mb-2 hover:underline" data-testid="back-link">
    <ArrowLeft className="h-5 w-5" /> {label}
  </Link>
);

export const AddButton = ({ children, small, ...p }) => (
  <button {...p} className={`${small ? "h-12 px-4 text-sm" : "h-14 px-6"} shrink-0 rounded-xl bg-[#1E4D2B] text-white font-semibold flex items-center justify-center gap-2 hover:bg-[#163A20] active:scale-[0.98] transition-[background-color,transform]`}>
    <Plus className="h-5 w-5" /> {children}
  </button>
);

export const ActionButton = ({ children, icon: Icon, variant = "outline", ...p }) => {
  const v = {
    outline: "bg-white border-2 border-[#E2DDD3] text-[#1B2A26] hover:border-[#1E4D2B] hover:bg-[#E8F5E9]",
    danger: "bg-white border-2 border-[#E2DDD3] text-[#B05A40] hover:border-[#B05A40] hover:bg-[#C86D51]/10",
    primary: "bg-[#1E4D2B] text-white hover:bg-[#163A20]",
    earth: "bg-[#C86D51] text-white hover:bg-[#B05A40]",
  }[variant];
  return (
    <button {...p} className={`h-12 px-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors active:scale-[0.98] disabled:opacity-60 ${v}`}>
      {Icon && <Icon className="h-5 w-5" />} {children}
    </button>
  );
};

export const SearchInput = ({ value, onChange, placeholder, testId }) => (
  <div className="relative flex-1">
    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#5C6B66]" />
    <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testId}
      className={`${inputCls} bg-white pl-12`} />
  </div>
);

export const ListCard = ({ to, icon: Icon, title, subtitle, meta, badge, testId }) => (
  <Link to={to} data-testid={testId}
    className="group min-w-0 bg-white rounded-2xl border border-[#E2DDD3] p-4 flex items-center gap-4 hover:border-[#1E4D2B] transition-colors active:scale-[0.99]">
    {Icon && <span className="h-12 w-12 rounded-xl bg-[#E8F5E9] text-[#1E4D2B] flex items-center justify-center shrink-0"><Icon className="h-6 w-6" /></span>}
    <div className="min-w-0 flex-1">
      <p className="font-semibold text-[#1B2A26] truncate">{title}</p>
      {subtitle && <p className="text-sm text-[#5C6B66] truncate">{subtitle}</p>}
      {meta && <p className="text-xs text-[#5C6B66] mt-1">{meta}</p>}
    </div>
    {badge}
    <ChevronRight className="h-5 w-5 text-[#5C6B66] shrink-0" />
  </Link>
);

export const ConfirmDelete = ({ open, onOpenChange, title, description, onConfirm }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent className="rounded-3xl">
      <AlertDialogHeader>
        <AlertDialogTitle>{title}</AlertDialogTitle>
        <AlertDialogDescription>{description}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel className="h-12" data-testid="confirm-cancel">Cancelar</AlertDialogCancel>
        <AlertDialogAction className="h-12 bg-[#B05A40] hover:bg-[#8f4733]" onClick={onConfirm} data-testid="confirm-delete">Excluir</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const DetailHeader = ({ eyebrow, title, subtitle, icon: Icon, actions }) => (
  <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6 fade-up">
    <div className="flex items-start gap-4 min-w-0">
      {Icon && <span className="h-14 w-14 rounded-2xl bg-[#1E4D2B] text-white flex items-center justify-center shrink-0"><Icon className="h-7 w-7" /></span>}
      <div className="min-w-0">
        {eyebrow && <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C86D51]">{eyebrow}</p>}
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight break-words" data-testid="page-title">{title}</h1>
        {subtitle && <p className="mt-1 text-[#5C6B66]">{subtitle}</p>}
      </div>
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);
